import { getCookie } from 'tiny-cookie';
import { set } from 'lodash-es';

import initializeClient from '@nuuly-ui/core-ssr/src/entry-client.js';
import createFeatureFlagManager from '~coreModules/core/js/feature-manager';

import { NUU_COOKIE_CLIENT_ID } from '~coreModules/core/js/es5/constants';
import { getWindowProperty } from '~coreModules/core/js/utils';
import { shouldPreventGlobalLoader } from '~coreModules/core/js/router-utils';
import { setNewRelicUserId, setNewRelicUserAttributes } from '~coreModules/core/js/newrelic-browser-utils';

import {
    CLEAR_ROUTING_ERROR,
    SET_ROUTE_IS_LOADING,
    SET_500_ERROR,
    SET_404_ERROR,
} from '~coreModules/core/js/store';
import { COMPLIANCE_MODULE_NAME } from '~coreModules/compliance/js/compliance-store';

import getResolvedSplitClient from '~coreModules/core/js/split-client';

let errorOccurred;
let unsubscriber;

// Middleware that will run before fetchData on every client side route
export async function globalMiddleware(to, from, store) {
    errorOccurred = false;
    unsubscriber = store.subscribe(({ type }) => {
        if (type === SET_500_ERROR || type === SET_404_ERROR) {
            errorOccurred = true;
        }
    });

    /* prevent global loading state */
    if (!shouldPreventGlobalLoader(to, from)) {
        store.commit(SET_ROUTE_IS_LOADING, true);
    }

    return Promise.resolve();
}

// Middleware that will run after fetchData on every client side route
export function globalPostMiddleware(to, from, store) {
    unsubscriber();
    // Only clear out errors if the last fetchData pass found no errors
    if (!errorOccurred && store.state.routingError) {
        store.commit(CLEAR_ROUTING_ERROR, null);
    }
    store.commit(SET_ROUTE_IS_LOADING, false);
    return Promise.resolve();
}

/**
 * Initialize the feature manager
 * @param {Object} params - Feature manager initialization parameters
 * @param {Object} params.initialValues - Initial feature flag values
 * @param {Object} params.config - Feature flag configuration
 * @param {boolean} params.isServer - Whether running in server context
 * @param {Object} params.splitClient - Initialized Split client
 * @param {Object} params.splitData - Split-related data
 * @param {Object} params.logger - Logger instance
 * @returns {Object} Feature manager instance
 */
function initializeFeatureManager({ initialValues, config, isServer, splitClient, splitData, logger }) {
    const featureManager = createFeatureFlagManager({
        initialFeatureValues: initialValues || {},
        featureConfig: config,
        isServer,
        splitClient,
        splitData: splitData || {},
        logger,
    });

    window.nuu = window.nuu || {};
    // Set feature manager on window for debugging
    Object.defineProperty(window.nuu, 'featureManager', {
        value: featureManager,
        writable: false,
        configurable: false,
        enumerable: true,
    });

    return featureManager;
}

function setSplitUserConsentAndReplaySSRImpressions(featureManager, splitFactory, shouldTrack, logger) {
    if (splitFactory) {
        splitFactory.UserConsent.setStatus(!!shouldTrack);
        logger.log('setting split user consent: ', splitFactory.UserConsent.getStatus());
    }

    featureManager.replaySsrSplitImpressions();
}

export async function initializeClientWithOpts(createApp, clientOpts) {
    const opts = {
        hmr: process.env.NODE_ENV === 'local',
        logger: console,
        middleware: globalMiddleware,
        postMiddleware: globalPostMiddleware,
        shouldProcessRouteUpdateDefaults: {
            path: true,
            query: true,
            hash: false,
        },
        ...clientOpts,
    };

    // using tiny-cookie getCookie directly here since we dont have access to the isomorphic cookie interface yet,
    // nor do we need it since this is browser only.
    const clientId = getCookie(NUU_COOKIE_CLIENT_ID);
    // identify unique user ASAP so we can best determine # of users impacted by errors
    setNewRelicUserId(clientId);

    const runtimeConfig = getWindowProperty('runtimeConfig', opts.logger);
    const { featureValues, splitData } = getWindowProperty('featureData', opts.logger);
    const { identity: splitIdentity } = splitData || {};

    const {
        splitClient: resolvedSplitClient,
        splitFactory,
    } = (featureValues.useSplit.value ? await getResolvedSplitClient(
        runtimeConfig.splitSdkApiKeyBrowser,
        splitIdentity,
        featureValues.useSplit.value,
        opts.logger,
    ) : {}) || {};

    const featureManager = initializeFeatureManager({
        initialValues: featureValues,
        config: clientOpts.featureConfig,
        isServer: false,
        splitClient: resolvedSplitClient,
        splitData,
        logger: opts.logger,
    });

    initializeClient((context) => {
        Object.assign(context, {
            runtimeConfig,
            initialState: getWindowProperty('initialState', opts.logger),
            translations: getWindowProperty('initialTranslations', opts.logger),
            userState: getWindowProperty('userState', opts.logger),
            newRelic: window.NREUM || null,
            featureManager,
        });

        const app = createApp(context);
        const { store } = app;
        const shouldTrack = store.getters[`${COMPLIANCE_MODULE_NAME}/shouldTrack`];
        setNewRelicUserAttributes(store);
        setSplitUserConsentAndReplaySSRImpressions(featureManager, splitFactory, shouldTrack, opts.logger);

        // Add a reference to the app for debugging
        set(window, 'nuu.app', app);

        return app;
    }, opts);
}
